import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { wealthApi } from '@/api/wealthApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Pencil, Trash2, ChevronDown, ChevronUp, Camera,
  TrendingUp, TrendingDown, Landmark, AlertTriangle, X, Check,
  Info,
} from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (v) =>
  '$' + Math.abs(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtSigned = (v) =>
  (v >= 0 ? '+' : '-') + '$' + Math.abs(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const PRESET_COLORS = [
  '#059669','#0ea5e9','#8b5cf6','#f59e0b','#ec4899',
  '#14b8a6','#f97316','#6366f1','#84cc16','#ef4444',
];

function monthLabel(year, month) {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

// ---------------------------------------------------------------------------
// Small reusable components
// ---------------------------------------------------------------------------

function ColorPicker({ value, onChange }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {PRESET_COLORS.map(c => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-6 h-6 rounded-full border-2 transition-all ${value === c ? 'border-slate-800 scale-110' : 'border-transparent'}`}
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  );
}

function StatCard({ label, value, sub, color = 'slate', icon: Icon }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700',
    rose: 'bg-rose-50 text-rose-700',
    blue: 'bg-blue-50 text-blue-700',
    slate: 'bg-slate-50 text-slate-700',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        {Icon && <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>}
      </div>
      <p className={`text-xl font-bold ${color === 'emerald' ? 'text-emerald-700' : color === 'rose' ? 'text-rose-600' : 'text-slate-800'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Class + item management panel
// ---------------------------------------------------------------------------

function ClassPanel({ title, classes, onAddClass, onEditClass, onDeleteClass, onAddItem, onEditItem, onDeleteItem, itemKey, itemValueKey, itemValueLabel, accentColor }) {
  const [expandedClass, setExpandedClass] = useState(null);
  const [addingClass, setAddingClass] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [addingItem, setAddingItem] = useState(null); // classId
  const [editingItem, setEditingItem] = useState(null); // item object
  const [classForm, setClassForm] = useState({ name: '', color: accentColor });
  const [itemForm, setItemForm] = useState({ name: '', [itemValueKey]: '', is_cash: false, notes: '' });

  const openAddClass = () => { setClassForm({ name: '', color: accentColor }); setAddingClass(true); setEditingClass(null); };
  const openEditClass = (cls) => { setClassForm({ name: cls.name, color: cls.color }); setEditingClass(cls); setAddingClass(false); };
  const openAddItem = (classId) => { setItemForm({ name: '', [itemValueKey]: '', is_cash: false, notes: '' }); setAddingItem(classId); setEditingItem(null); };
  const openEditItem = (item) => {
    setItemForm({ name: item.name, [itemValueKey]: item[itemValueKey], is_cash: item.is_cash || false, notes: item.notes || '' });
    setEditingItem(item);
    setAddingItem(null);
  };

  const submitClass = () => {
    if (!classForm.name.trim()) return;
    if (editingClass) {
      onEditClass(editingClass.id, classForm);
    } else {
      onAddClass(classForm);
    }
    setAddingClass(false);
    setEditingClass(null);
  };

  const submitItem = (classId) => {
    const val = parseFloat(itemForm[itemValueKey]) || 0;
    const data = { name: itemForm.name, [itemValueKey]: val, notes: itemForm.notes || null };
    if (itemValueKey === 'current_value') data.is_cash = itemForm.is_cash;
    if (editingItem) {
      onEditItem(editingItem.id, data);
    } else {
      onAddItem({ ...data, [itemKey]: classId });
    }
    setAddingItem(null);
    setEditingItem(null);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openAddClass}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Class
        </Button>
      </div>

      {/* Add/edit class form */}
      {(addingClass || editingClass) && (
        <div className="p-4 border-b border-slate-100 bg-slate-50 space-y-3">
          <p className="text-xs font-medium text-slate-600">{editingClass ? 'Edit class' : 'New class'}</p>
          <Input
            placeholder="Class name (e.g. Property)"
            value={classForm.name}
            onChange={e => setClassForm(f => ({ ...f, name: e.target.value }))}
            className="h-8 text-sm"
            autoFocus
          />
          <ColorPicker value={classForm.color} onChange={c => setClassForm(f => ({ ...f, color: c }))} />
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" onClick={submitClass}>
              <Check className="w-3 h-3 mr-1" /> {editingClass ? 'Save' : 'Add'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setAddingClass(false); setEditingClass(null); }}>
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {classes.length === 0 && !addingClass && (
        <div className="p-6 text-center text-sm text-slate-400">No classes yet — add one above</div>
      )}

      <div className="divide-y divide-slate-50">
        {classes.map(cls => {
          const items = cls[itemKey === 'assetClassId' ? 'assets' : 'liabilities'] || [];
          const isExpanded = expandedClass === cls.id;
          const total = items.reduce((s, i) => s + (i[itemValueKey] || 0), 0);

          return (
            <div key={cls.id}>
              {/* Class row */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedClass(isExpanded ? null : cls.id)}
              >
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cls.color }} />
                <span className="flex-1 text-sm font-medium text-slate-700">{cls.name}</span>
                <span className="text-sm text-slate-500 font-medium">{fmt(total)}</span>
                <span className="text-xs text-slate-400">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                <div className="flex gap-1 ml-1" onClick={e => e.stopPropagation()}>
                  <button onClick={() => openEditClass(cls)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button onClick={() => onDeleteClass(cls.id)} className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>

              {/* Items */}
              {isExpanded && (
                <div className="bg-slate-50 border-t border-slate-100">
                  {items.map(item => (
                    <div key={item.id}>
                      {editingItem?.id === item.id ? (
                        <div className="px-6 py-3 space-y-2">
                          <Input placeholder="Name" value={itemForm.name} onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" autoFocus />
                          <div className="flex gap-2 items-center">
                            <Input
                              placeholder={itemValueLabel}
                              type="number"
                              value={itemForm[itemValueKey]}
                              onChange={e => setItemForm(f => ({ ...f, [itemValueKey]: e.target.value }))}
                              className="h-8 text-sm"
                            />
                            {itemValueKey === 'current_value' && (
                              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                                <input type="checkbox" checked={itemForm.is_cash} onChange={e => setItemForm(f => ({ ...f, is_cash: e.target.checked }))} />
                                Cash account
                              </label>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 text-xs" onClick={() => submitItem(cls.id)}>
                              <Check className="w-3 h-3 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingItem(null)}>
                              <X className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 px-6 py-2.5 hover:bg-white transition-colors">
                          <span className="flex-1 text-sm text-slate-600">{item.name}</span>
                          {item.is_cash && <Badge variant="outline" className="text-xs py-0 h-4 text-emerald-600 border-emerald-200">cash</Badge>}
                          <span className="text-sm font-medium text-slate-700">{fmt(item[itemValueKey])}</span>
                          <div className="flex gap-1">
                            <button onClick={() => openEditItem(item)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button onClick={() => onDeleteItem(item.id)} className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-500">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add item form */}
                  {addingItem === cls.id ? (
                    <div className="px-6 py-3 space-y-2 border-t border-slate-100">
                      <Input placeholder="Name" value={itemForm.name} onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-sm" autoFocus />
                      <div className="flex gap-2 items-center">
                        <Input
                          placeholder={itemValueLabel}
                          type="number"
                          value={itemForm[itemValueKey]}
                          onChange={e => setItemForm(f => ({ ...f, [itemValueKey]: e.target.value }))}
                          className="h-8 text-sm"
                        />
                        {itemValueKey === 'current_value' && (
                          <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                            <input type="checkbox" checked={itemForm.is_cash} onChange={e => setItemForm(f => ({ ...f, is_cash: e.target.checked }))} />
                            Cash account
                          </label>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 text-xs" onClick={() => submitItem(cls.id)}>
                          <Check className="w-3 h-3 mr-1" /> Add
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAddingItem(null)}>
                          <X className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="w-full text-left px-6 py-2 text-xs text-slate-400 hover:text-emerald-600 hover:bg-white transition-colors flex items-center gap-1"
                      onClick={() => openAddItem(cls.id)}
                    >
                      <Plus className="w-3 h-3" /> Add item
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Snapshot panel
// ---------------------------------------------------------------------------

function SnapshotPanel({ assets, liabilities, snapshots, onTakeSnapshot, onDeleteSnapshot, isTaking }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [cashHint, setCashHint] = useState(null);
  const [loadingHint, setLoadingHint] = useState(false);

  const totalAssets = assets.reduce((s, a) => s + (a.current_value || 0), 0);
  const totalLiabilities = liabilities.reduce((s, l) => s + (l.current_balance || 0), 0);
  const netWorth = totalAssets - totalLiabilities;

  const existingSnapshot = snapshots.find(s => s.year === year && s.month === month);

  const fetchCashHint = async () => {
    setLoadingHint(true);
    try {
      const data = await wealthApi.getCashHint(year, month);
      setCashHint(data);
    } catch {
      setCashHint(null);
    } finally {
      setLoadingHint(false);
    }
  };

  // Find cash assets
  const cashAssets = assets.filter(a => a.is_cash);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 text-sm">Record Monthly Snapshot</h3>
        <Camera className="w-4 h-4 text-slate-400" />
      </div>

      {/* Current totals preview */}
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-emerald-50 rounded-lg p-2">
          <p className="text-xs text-emerald-600 font-medium">Assets</p>
          <p className="text-sm font-bold text-emerald-700">{fmt(totalAssets)}</p>
        </div>
        <div className="bg-rose-50 rounded-lg p-2">
          <p className="text-xs text-rose-600 font-medium">Liabilities</p>
          <p className="text-sm font-bold text-rose-700">{fmt(totalLiabilities)}</p>
        </div>
        <div className={`rounded-lg p-2 ${netWorth >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
          <p className={`text-xs font-medium ${netWorth >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Worth</p>
          <p className={`text-sm font-bold ${netWorth >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{fmt(netWorth)}</p>
        </div>
      </div>

      {/* Cash hint */}
      {cashAssets.length > 0 && (
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-slate-600 font-medium mb-1">Cash account balance hint</p>
              <p className="text-xs text-slate-500 mb-2">
                You have {cashAssets.length} cash account{cashAssets.length > 1 ? 's' : ''} (
                {cashAssets.map(a => a.name).join(', ')}). Update their values before snapshotting, or use the last recorded balance from your transactions.
              </p>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={fetchCashHint} disabled={loadingHint}>
                {loadingHint ? 'Loading…' : `Use last balance for ${MONTH_NAMES[month - 1]} ${year}`}
              </Button>
              {cashHint?.balance != null && (
                <p className="text-xs text-emerald-600 mt-1.5 font-medium">
                  Last balance on {cashHint.date}: {fmt(cashHint.balance)} — update your cash asset value above to use this.
                </p>
              )}
              {cashHint?.balance == null && cashHint !== null && (
                <p className="text-xs text-slate-400 mt-1.5">No balance data found for that month in your transactions.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Month/year picker */}
      <div className="flex gap-2 items-center">
        <select
          value={month}
          onChange={e => setMonth(Number(e.target.value))}
          className="flex-1 h-8 text-sm border border-slate-200 rounded-md px-2 bg-white text-slate-700"
        >
          {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="w-24 h-8 text-sm border border-slate-200 rounded-md px-2 bg-white text-slate-700"
        >
          {Array.from({ length: 10 }, (_, i) => now.getFullYear() - 5 + i).map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {existingSnapshot && (
        <div className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-700">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          A snapshot for {MONTH_NAMES[month - 1]} {year} already exists — taking a new one will overwrite it.
        </div>
      )}

      <Button
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-sm"
        onClick={() => onTakeSnapshot(year, month)}
        disabled={isTaking || assets.length === 0}
      >
        <Camera className="w-4 h-4 mr-2" />
        {isTaking ? 'Saving…' : `Save Snapshot for ${MONTH_NAMES[month - 1]} ${year}`}
      </Button>

      {assets.length === 0 && (
        <p className="text-xs text-slate-400 text-center">Add at least one asset before taking a snapshot.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

function NetWorthChart({ snapshots }) {
  const data = snapshots.map(s => ({
    label: monthLabel(s.year, s.month),
    assets: s.total_assets,
    liabilities: s.total_liabilities,
    netWorth: s.net_worth,
  }));

  if (data.length < 2) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-800 text-sm mb-1">Net Worth Over Time</h3>
        <p className="text-xs text-slate-400">Record at least 2 monthly snapshots to see the trend.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-slate-800 text-sm mb-4">Net Worth Over Time</h3>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="gradNW" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false}
            tickFormatter={v => '$' + (v >= 1000000 ? (v/1000000).toFixed(1)+'M' : v >= 1000 ? (v/1000).toFixed(0)+'k' : v)} />
          <Tooltip formatter={(v, n) => [fmt(v), n === 'netWorth' ? 'Net Worth' : n]} />
          <Area type="monotone" dataKey="netWorth" stroke="#3b82f6" strokeWidth={2} fill="url(#gradNW)" name="netWorth" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function AssetsVsLiabilitiesChart({ snapshots }) {
  const data = snapshots.map(s => ({
    label: monthLabel(s.year, s.month),
    assets: s.total_assets,
    liabilities: s.total_liabilities,
  }));

  if (data.length < 2) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-slate-800 text-sm mb-4">Assets vs Liabilities</h3>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="gradA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#059669" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#059669" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradL" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#e11d48" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#e11d48" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false}
            tickFormatter={v => '$' + (v >= 1000000 ? (v/1000000).toFixed(1)+'M' : v >= 1000 ? (v/1000).toFixed(0)+'k' : v)} />
          <Tooltip formatter={(v, n) => [fmt(v), n === 'assets' ? 'Total Assets' : 'Total Liabilities']} />
          <Legend formatter={v => v === 'assets' ? 'Assets' : 'Liabilities'} />
          <Area type="monotone" dataKey="assets" stroke="#059669" strokeWidth={2} fill="url(#gradA)" />
          <Area type="monotone" dataKey="liabilities" stroke="#e11d48" strokeWidth={2} fill="url(#gradL)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function BreakdownPie({ title, data, valueKey, nameKey, emptyMsg }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-800 text-sm mb-2">{title}</h3>
        <p className="text-xs text-slate-400">{emptyMsg}</p>
      </div>
    );
  }

  // Aggregate by class
  const byClass = {};
  data.forEach(item => {
    const key = item.assetClass?.name || item.liabilityClass?.name || 'Other';
    const color = item.assetClass?.color || item.liabilityClass?.color || '#94a3b8';
    if (!byClass[key]) byClass[key] = { name: key, value: 0, color };
    byClass[key].value += item[valueKey] || 0;
  });
  const pieData = Object.values(byClass).filter(d => d.value > 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-slate-800 text-sm mb-2">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80}
            dataKey="value" nameKey="name" paddingAngle={2}>
            {pieData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => fmt(v)} />
          <Legend formatter={(v) => v} iconType="circle" iconSize={8}
            wrapperStyle={{ fontSize: '11px' }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function SnapshotHistory({ snapshots, onDelete }) {
  if (snapshots.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-slate-800 text-sm mb-3">Snapshot History</h3>
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {[...snapshots].reverse().map(s => (
          <div key={s.id} className="flex items-center gap-3 text-sm py-1.5 px-2 hover:bg-slate-50 rounded-lg">
            <span className="text-slate-500 w-20 flex-shrink-0">{monthLabel(s.year, s.month)}</span>
            <span className="flex-1 text-emerald-700 font-medium">{fmt(s.net_worth)}</span>
            <span className="text-slate-400 text-xs">{fmt(s.total_assets)} assets</span>
            <span className="text-slate-400 text-xs">{fmt(s.total_liabilities)} liabilities</span>
            <button onClick={() => onDelete(s.id)} className="p-1 rounded hover:bg-rose-50 text-slate-300 hover:text-rose-400">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Wealth page
// ---------------------------------------------------------------------------

export default function Wealth() {
  const queryClient = useQueryClient();

  const { data: assetClasses = [] } = useQuery({ queryKey: ['assetClasses'], queryFn: wealthApi.getAssetClasses });
  const { data: assets = [] } = useQuery({ queryKey: ['assets'], queryFn: wealthApi.getAssets });
  const { data: liabilityClasses = [] } = useQuery({ queryKey: ['liabilityClasses'], queryFn: wealthApi.getLiabilityClasses });
  const { data: liabilities = [] } = useQuery({ queryKey: ['liabilities'], queryFn: wealthApi.getLiabilities });
  const { data: snapshots = [] } = useQuery({ queryKey: ['wealthSnapshots'], queryFn: wealthApi.getSnapshots });

  const inv = (keys) => keys.forEach(k => queryClient.invalidateQueries({ queryKey: [k] }));

  // Asset class mutations
  const addAssetClass = useMutation({ mutationFn: wealthApi.createAssetClass, onSuccess: () => inv(['assetClasses']) });
  const editAssetClass = useMutation({ mutationFn: ({ id, data }) => wealthApi.updateAssetClass(id, data), onSuccess: () => inv(['assetClasses']) });
  const deleteAssetClass = useMutation({ mutationFn: wealthApi.deleteAssetClass, onSuccess: () => inv(['assetClasses', 'assets']) });

  // Asset mutations
  const addAsset = useMutation({ mutationFn: wealthApi.createAsset, onSuccess: () => inv(['assetClasses', 'assets']) });
  const editAsset = useMutation({ mutationFn: ({ id, data }) => wealthApi.updateAsset(id, data), onSuccess: () => inv(['assetClasses', 'assets']) });
  const deleteAsset = useMutation({ mutationFn: wealthApi.deleteAsset, onSuccess: () => inv(['assetClasses', 'assets']) });

  // Liability class mutations
  const addLiabilityClass = useMutation({ mutationFn: wealthApi.createLiabilityClass, onSuccess: () => inv(['liabilityClasses']) });
  const editLiabilityClass = useMutation({ mutationFn: ({ id, data }) => wealthApi.updateLiabilityClass(id, data), onSuccess: () => inv(['liabilityClasses']) });
  const deleteLiabilityClass = useMutation({ mutationFn: wealthApi.deleteLiabilityClass, onSuccess: () => inv(['liabilityClasses', 'liabilities']) });

  // Liability mutations
  const addLiability = useMutation({ mutationFn: wealthApi.createLiability, onSuccess: () => inv(['liabilityClasses', 'liabilities']) });
  const editLiability = useMutation({ mutationFn: ({ id, data }) => wealthApi.updateLiability(id, data), onSuccess: () => inv(['liabilityClasses', 'liabilities']) });
  const deleteLiability = useMutation({ mutationFn: wealthApi.deleteLiability, onSuccess: () => inv(['liabilityClasses', 'liabilities']) });

  // Snapshot mutations
  const takeSnapshot = useMutation({
    mutationFn: ({ year, month }) => wealthApi.createSnapshot(year, month),
    onSuccess: () => inv(['wealthSnapshots']),
  });
  const deleteSnapshot = useMutation({
    mutationFn: wealthApi.deleteSnapshot,
    onSuccess: () => inv(['wealthSnapshots']),
  });

  // Summary stats
  const totalAssets = assets.reduce((s, a) => s + (a.current_value || 0), 0);
  const totalLiabilities = liabilities.reduce((s, l) => s + (l.current_balance || 0), 0);
  const netWorth = totalAssets - totalLiabilities;

  const prevSnapshot = snapshots.length >= 2 ? snapshots[snapshots.length - 2] : null;
  const netWorthChange = prevSnapshot ? netWorth - prevSnapshot.net_worth : null;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Wealth Tracker</h1>
        <p className="text-sm text-slate-400 mt-0.5">Track assets, liabilities and net worth over time</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Assets" value={fmt(totalAssets)} icon={TrendingUp} color="emerald" />
        <StatCard label="Total Liabilities" value={fmt(totalLiabilities)} icon={TrendingDown} color="rose" />
        <StatCard
          label="Net Worth"
          value={fmt(netWorth)}
          sub={netWorthChange !== null ? `${fmtSigned(netWorthChange)} vs prev snapshot` : 'No previous snapshot'}
          icon={Landmark}
          color={netWorth >= 0 ? 'blue' : 'slate'}
        />
        <StatCard label="Snapshots" value={snapshots.length} sub={snapshots.length > 0 ? `Latest: ${monthLabel(snapshots[snapshots.length-1].year, snapshots[snapshots.length-1].month)}` : 'None yet'} icon={Camera} color="slate" />
      </div>

      {/* Charts row */}
      {snapshots.length >= 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <NetWorthChart snapshots={snapshots} />
          <AssetsVsLiabilitiesChart snapshots={snapshots} />
        </div>
      )}

      {/* Breakdown pies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BreakdownPie
          title="Asset Breakdown"
          data={assets}
          valueKey="current_value"
          emptyMsg="Add assets to see the breakdown"
        />
        <BreakdownPie
          title="Liability Breakdown"
          data={liabilities}
          valueKey="current_balance"
          emptyMsg="Add liabilities to see the breakdown"
        />
      </div>

      {/* Management + snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <ClassPanel
            title="Assets"
            classes={assetClasses}
            onAddClass={(data) => addAssetClass.mutate(data)}
            onEditClass={(id, data) => editAssetClass.mutate({ id, data })}
            onDeleteClass={(id) => deleteAssetClass.mutate(id)}
            onAddItem={(data) => addAsset.mutate(data)}
            onEditItem={(id, data) => editAsset.mutate({ id, data })}
            onDeleteItem={(id) => deleteAsset.mutate(id)}
            itemKey="assetClassId"
            itemValueKey="current_value"
            itemValueLabel="Current value ($)"
            accentColor="#059669"
          />
          <ClassPanel
            title="Liabilities"
            classes={liabilityClasses}
            onAddClass={(data) => addLiabilityClass.mutate(data)}
            onEditClass={(id, data) => editLiabilityClass.mutate({ id, data })}
            onDeleteClass={(id) => deleteLiabilityClass.mutate(id)}
            onAddItem={(data) => addLiability.mutate(data)}
            onEditItem={(id, data) => editLiability.mutate({ id, data })}
            onDeleteItem={(id) => deleteLiability.mutate(id)}
            itemKey="liabilityClassId"
            itemValueKey="current_balance"
            itemValueLabel="Current balance ($)"
            accentColor="#e11d48"
          />
        </div>
        <div className="space-y-5">
          <SnapshotPanel
            assets={assets}
            liabilities={liabilities}
            snapshots={snapshots}
            onTakeSnapshot={(year, month) => takeSnapshot.mutate({ year, month })}
            isTaking={takeSnapshot.isPending}
          />
          <SnapshotHistory snapshots={snapshots} onDelete={(id) => deleteSnapshot.mutate(id)} />
        </div>
      </div>
    </div>
  );
}
